import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableFooter from "@mui/material/TableFooter";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { DailyExpenseMonthRow } from "../../types/daily-expense.type";
import { formatNumber, numberWithCommas } from "../../utils";
import DailyExpenseStatusChip from "./DailyExpenseStatusChip";
import { EMPTY_CELL_TEXT, dailyExpenseDayPath } from "./constants";
import { chipKindOf, listActionLabel } from "./dailyExpenseStatus";
import { sumMonthRows } from "./dailyExpenseMonth";
import { formatBusinessDate, formatTimestamp, weekdayShortOf } from "./format";
import {
  CLICKABLE_ROW_SX,
  FOOT_ROW_SX,
  HEAD_ROW_SX,
  TABLE_SX,
  tableContainerSx,
} from "./tableStyles";

const TABLE_MAX_HEIGHT = 560;

interface MonthTableProps {
  rows: DailyExpenseMonthRow[];
  isMonthClosed: boolean;
}

/** One row per day the branch ran (or wrote a document), newest first; a click opens the day. */
export default function MonthTable({ rows, isMonthClosed }: Readonly<MonthTableProps>) {
  const navigate = useNavigate();
  const totals = sumMonthRows(rows);
  const openDay = (row: DailyExpenseMonthRow) => () =>
    navigate(dailyExpenseDayPath(row.business_date));

  const amountText = (row: DailyExpenseMonthRow, amount: number) =>
    row.status === null ? EMPTY_CELL_TEXT : formatNumber(amount);
  const amountColor = (row: DailyExpenseMonthRow) =>
    row.status === null ? "text.disabled" : "text.primary";

  return (
    <TableContainer sx={tableContainerSx(TABLE_MAX_HEIGHT)}>
      <Table stickyHeader size="small" sx={TABLE_SX}>
        <TableHead>
          <TableRow sx={HEAD_ROW_SX}>
            <TableCell>วันที่</TableCell>
            <TableCell>สถานะ</TableCell>
            <TableCell align="right">สายที่วิ่ง</TableCell>
            <TableCell align="right">ฝ่ายขาย</TableCell>
            <TableCell align="right">ฝ่ายบริหาร</TableCell>
            <TableCell align="right">รวม</TableCell>
            <TableCell>ผู้ส่ง</TableCell>
            <TableCell>เวลาส่ง</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.business_date} onClick={openDay(row)} sx={CLICKABLE_ROW_SX}>
              <TableCell>
                {formatBusinessDate(row.business_date)}{" "}
                <Box component="span" sx={{ color: "text.secondary" }}>
                  {weekdayShortOf(row.business_date)}
                </Box>
              </TableCell>
              <TableCell>
                <DailyExpenseStatusChip kind={chipKindOf(row.status, isMonthClosed)} />
              </TableCell>
              <TableCell align="right">{numberWithCommas(row.route_count)}</TableCell>
              <TableCell align="right" sx={{ color: amountColor(row) }}>
                {amountText(row, row.sales_total)}
              </TableCell>
              <TableCell align="right" sx={{ color: amountColor(row) }}>
                {amountText(row, row.admin_total)}
              </TableCell>
              <TableCell align="right" sx={{ color: amountColor(row), fontWeight: 600 }}>
                {amountText(row, row.total)}
              </TableCell>
              <TableCell sx={{ color: "text.secondary" }}>
                {row.submitted_by_name ?? EMPTY_CELL_TEXT}
              </TableCell>
              <TableCell sx={{ color: "text.secondary" }}>
                {formatTimestamp(row.submitted_at)}
              </TableCell>
              <TableCell align="right" sx={{ color: "primary.main", fontWeight: 500 }}>
                {listActionLabel(row.status, isMonthClosed)} ›
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow sx={FOOT_ROW_SX}>
            <TableCell colSpan={3}>รวม {numberWithCommas(rows.length)} วัน</TableCell>
            <TableCell align="right">{formatNumber(totals.sales_total)}</TableCell>
            <TableCell align="right">{formatNumber(totals.admin_total)}</TableCell>
            <TableCell align="right">{formatNumber(totals.total)}</TableCell>
            <TableCell colSpan={3} />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}
